import { ref, computed, watch, inject, onMounted } from 'vue'
import { defineStore } from 'pinia'
import { groupBy, orderBy } from 'lodash'

import { WSInstance, WSResponse } from '@/services/ws/utils/types'
import type { AIMessage, AIConversation, PaginatedResponse } from '@types'
import type { AIChatMessageData } from '../utils/types'
import { AIType } from '../utils/enums'

import { ASSET_FIELD } from '@/const'
import { VALUE_DELIMITER } from '../utils/const'

import api from '@/store/api'

import { generateId } from '@/entities/utils/helpers'
import { useUISettings } from '@/hooks/uiSettings'
import { removeStoreListItem } from '@/store/utils/helpers'
import { dateTimeToString, stringToDateTime } from '@/helpers/dates'
import { handleCatchedError } from '@/helpers/common'
import { destroyTread, fetchThread, handleResponse, prepareMessages } from '../utils/helpers'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useUserStore } from '@/store/user'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'
import { useRepositoriesStore } from '@/store/repositories/index'

export const useAIAgentStore = defineStore('ai-agent', () => {
  // INIT
  const assetsBunchStore = useAssetsBunchStore()
  const userStore = useUserStore()
  const repositoriesStore = useRepositoriesStore()
  const transactionsSettingsStore = useTransactionsSettingsStore()

  const getTmpThreadId = () => `tmp_${generateId()}`

  const currentThreadId = useUISettings<string>(['ai-agent-thread-id'], getTmpThreadId())

  const ws = inject<WSInstance>('ws')

  const messages = ref<AIMessage[]>([])
  const conversations = ref<AIConversation[]>([])

  const loading = ref(false)
  const initFlag = ref(false)

  const errorMessage = ref<string>()

  const assetsBunchList = computed(() => assetsBunchStore.getList)

  const settingsList = computed(() => transactionsSettingsStore.getList)

  const accountsList = computed(() => {
    const accounts = []
    for (const asset of assetsBunchList.value.values()) {
      const type = asset.field<string>(ASSET_FIELD.TYPE).value
      if (!allTypes.value.includes(type)) continue
      const name = asset.field<string>(ASSET_FIELD.NAME).value
      const ticker = asset.field<string>(ASSET_FIELD.TICKER).value
      accounts.push(`{ name: "${name}", type: "${type}", ticker: "${ticker}" }`)
    }
    return accounts.join(', ')
  })

  const allTypes = computed(() => {
    return [
      ...new Set([
        ...(addPositionAccountsTypes.value || []),
        ...(subscribeAndCommitAccountsTypes.value || []),
        ...(subscribeAndFundAccountsTypes.value || []),
        ...(addLiabilityAccountsTypes.value || []),
      ]),
    ]
  })

  const addPositionAccountsTypes = computed(
    () =>
      settingsList.value.find(item => item.name === 'add position')?.entries[0]
        .account?.includes,
  )
  const subscribeAndCommitAccountsTypes = computed(
    () =>
      settingsList.value.find(item => item.name === 'subscribe and commit')
        ?.entries[0].account?.includes,
  )
  const subscribeAndFundAccountsTypes = computed(
    () =>
      settingsList.value.find(item => item.name === 'subscribe and fund')
        ?.entries[0].account?.includes,
  )
  const addLiabilityAccountsTypes = computed(
    () =>
      settingsList.value.find(item => item.name === 'add liability')?.entries[0]
        .account?.includes,
  )
  const currenciesList = computed(() =>
    orderBy(repositoriesStore.getCurrencies.map(item => item.ticker)),
  )

  // GETTERS
  const getErrorMessage = computed(() => errorMessage.value)

  const getMessages = computed(() => messages.value)

  const getConversations = computed(() => groupBy(orderBy(conversations.value, 'last_updated', 'desc'), item => {
    return dateTimeToString(stringToDateTime(item.last_updated), { relative: true })
  }))

  const getCurrentThreadId = computed(() => currentThreadId.value)

  const isNewThread = computed(() => getCurrentThreadId.value.startsWith('tmp_'))

  // SETTERS

  const setErrorMessage = (data?: string) => {
    errorMessage.value = data
  }

  const setMessages = (data: AIMessage[]) => {
    messages.value = data
  }

  const pushMessage = (data: AIMessage) => {
    messages.value = [...messages.value, data]
  }

  const setConversations = (data: AIConversation[]) => {
    conversations.value = data
  }

  const setConversationLastUpdated = (threadId: string) => {
    const conversation = conversations.value.find(item => item.thread_id === threadId)
    if (!conversation) return
    conversation.last_updated = new Date().toISOString()
  }

  const setCurrentThreadId = (data: string) => {
    setErrorMessage(undefined)
    currentThreadId.value = data
  }

  // ACTIONS

  const fetch = async (threadId: string) => {
    loading.value = true
    try {
      const result = await fetchThread(threadId)
      setMessages(result)
    } catch (e) {
      handleCatchedError(e as string)
    } finally {
      loading.value = false
    }
  }

  const fetchConversations = async () => {
    try {
      const result = await api.get<PaginatedResponse<AIConversation>>(
        'ai_conversations',
      )
      setConversations(result.data.data)
    } catch (e) {
      handleCatchedError(e as string)
    } finally {
      initFlag.value = true
    }
  }

  const destroy = async (threadId: string) => {
    try {
      await destroyTread(threadId)
      if (getCurrentThreadId.value === threadId) {
        reset()
      }
      removeStoreListItem<AIConversation>(conversations.value, threadId, 'thread_id')
      if (getCurrentThreadId.value === threadId) {
        reset(false)
      }
    } catch (e) {
      handleCatchedError(e as string)
    }
  }

  const submit = async (content: string, pushToStore = true) => {
    const threadId = isNewThread.value ? undefined : getCurrentThreadId.value

    const { messages, message } = prepareMessages(content)
    pushToStore && pushMessage(message)

    const dynamicContext = {
      accounts_list: accountsList.value,
      currencies_list: `"${currenciesList.value.join('\", \"')}"`,
      add_position_accounts_types: `"${(addPositionAccountsTypes.value || []).join(VALUE_DELIMITER)}"`,
      subscribe_and_commit_accounts_types: `"${(subscribeAndCommitAccountsTypes.value || []).join(VALUE_DELIMITER)}"`,
      subscribe_and_fund_accounts_types: `"${(subscribeAndFundAccountsTypes.value || []).join(VALUE_DELIMITER)}"`,
      add_liability_accounts_types: `"${(addLiabilityAccountsTypes.value || []).join(VALUE_DELIMITER)}"`,
    }

    const messageData: AIChatMessageData = {
      ai_type: AIType.AGENT,
      messages,
      thread_id: threadId,
      dynamic_context: dynamicContext,
    }

    loading.value = true
    ws?.send('ai', 'chat', getCurrentThreadId.value, messageData)
  }

  const retry = () => {
    const content = messages.value.at(-1)?.content
    setErrorMessage(undefined)
    if (!content) return
    submit(content, false)
  }

  const reset = (withConversations = true) => {
    messages.value = []
    loading.value = false
    setCurrentThreadId(getTmpThreadId())
    if (withConversations) {
      conversations.value = []
    }
  }

  watch(
    () => userStore.isAuthenticated,
    value => {
      if (!value) return
      ws?.subscribe(
        'ai',
        'chat',
        async (message: WSResponse<string>, requestId: string) => {
          try {
            handleResponse(requestId, getCurrentThreadId.value, message, setErrorMessage, setCurrentThreadId, pushMessage, setConversationLastUpdated)
          } catch (e) {
            handleCatchedError(e as string)
          } finally {
            loading.value = false
          }
        },
      )
    },
    { immediate: true },
  )

  onMounted(() => {
    if (isNewThread.value) return
    fetch(getCurrentThreadId.value)
  })

  return {
    initFlag,
    loading,

    fetch,
    fetchConversations,

    getMessages,
    getConversations,
    getCurrentThreadId,
    getErrorMessage,

    setCurrentThreadId,

    submit,
    retry,

    destroy,

    reset,
  }
})
